/**
 * Mulberry Product Protection — Houndsy Kibble Dispenser only.
 * Per selected dispenser variant: fetch offers, render UI, store selections for cart.
 * @see https://docs.getmulberry.com/reference/initialization
 */

// Source of truth: the `mulberry_eligible_handles` theme setting, forwarded via window.globalSpace.kibbleDispenserHandles.
// The literal is only a defensive fallback for the base product if globalSpace is unavailable.
const MULBERRY_ELIGIBLE_HANDLES = (window.globalSpace && Array.isArray(window.globalSpace.kibbleDispenserHandles) && window.globalSpace.kibbleDispenserHandles.length)
  ? window.globalSpace.kibbleDispenserHandles
  : ['houndsy-kibble-dispenser'];

/** @type {Record<string, { warranty_offer_id: string, plan_name: string, priceCents: number, duration_months?: string }>} */
window.selectedMulberryWarranties = window.selectedMulberryWarranties || {};

class MulberryIntegration {
  constructor() {
    this.config = null;
    this.initialized = false;
    this.syncing = false;
    this.coreInitPromise = null;
    this.coreInitRetried = false;
    /** @type {Map<string, { offers: object[], productInfo: object }>} */
    this.offersCache = new Map();
    /** instanceKey -> variantId */
    this.instanceVariantMap = new Map();
    /** variantIds where the customer chose a protection plan */
    this.resolvedVariantIds = new Set();
    this.dispenserObserver = null;
    this._warrantySeriesMap = undefined;
    this.prevVariantByInstanceKey = new Map();
  }

  // ---------------------------------------------------------------------------
  // Product eligibility (Req 1)
  // ---------------------------------------------------------------------------

  isEligibleProduct() {
    const fromConfig = this.config?.eligibleProduct;
    if (!fromConfig?.handle) return false;

    return MULBERRY_ELIGIBLE_HANDLES.includes(fromConfig.handle);
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------

  init() {
    const configEl = document.getElementById('mulberry-product-config');
    if (!configEl) return;

    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[mulberry] invalid product config', error);
      return;
    }

    if (!this.isEligibleProduct()) {
      console.log('[mulberry] skipped — product not eligible', {
        productId: window.globalSpace?.product?.id,
        handle: window.globalSpace?.product?.handle
      });
      return;
    }

    if (!this.config.publicToken) {
      console.warn('[mulberry] public token missing');
      return;
    }

    this.bindEvents();
    this.observeDispenserDom();
    this.bootstrap();
  }

  async bootstrap() {
    try {
      await this.waitForSdk();
      await this.ensureCoreInit();
      this.initialized = true;
      await this.syncFromDom();
    } catch (error) {
      console.error('[mulberry] bootstrap failed', error);
      this.updateCareCardVisibility();
    }
  }

  bindEvents() {
    if (typeof eventBus !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      eventBus.subscribe(PUB_SUB_EVENTS.variantChange, (eventData) => {
        const { container } = eventData.data || {};
        if (!this.isDispenserVariantContext(container)) return;
        this.syncFromDom();
      });

      eventBus.subscribe(PUB_SUB_EVENTS.productATC, () => {
        console.log('Selected Warranties', window.selectedMulberryWarranties);
      });
    }

    document.addEventListener(
      'change',
      (event) => {
        if (!(event.target instanceof HTMLInputElement)) return;
        if (!event.target.matches('[data-warranty-checkbox]')) return;
        this.syncCareWarrantySelections();
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (!this.shouldValidateBeforeSubmit(event.target)) return;
        if (this.allRequiredWarrantiesSelected()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        window.notificationEle?.updateNotification?.(
          'Protection plan',
          'Please add Houndsy Care for each dispenser.',
          { type: 'error', timeout: 6000 }
        );
      },
      true
    );
  }

  shouldValidateBeforeSubmit(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (!form.matches('[data-type="add-to-cart-form"]')) return false;
    if (!this.initialized || !this.config?.requireWarranty) return false;

    const submitButton = form.querySelector('[type="submit"]');
    return Boolean(submitButton?.hasAttribute('data-buy-dispenser-main-btn'));
  }

  allRequiredWarrantiesSelected() {
    // Houndsy Care is optional — unchecked means add to cart without warranty.
    if (!this.isHoundsyCareSelected()) return true;

    const instances = this.getDispenserInstances();
    const withOffers = instances.filter((inst) => {
      const cached = this.offersCache.get(String(inst.variantId));
      return cached?.offers?.length > 0;
    });
    if (!withOffers.length) return true;

    return withOffers.every((inst) => this.resolvedVariantIds.has(String(inst.variantId)));
  }

  isHoundsyCareSelected() {
    const checkbox = document.querySelector(
      '[data-warranty-card][data-warranty-owner-key="main"] [data-warranty-checkbox]'
    );
    return Boolean(checkbox?.checked);
  }

  pickDefaultOffer(offers) {
    if (!offers?.length) return null;
    return offers.find((offer) => offer.recommended) || offers[0];
  }

  syncCareWarrantySelections() {
    if (!this.initialized) return;

    if (!this.isHoundsyCareSelected()) {
      this.getDispenserInstances().forEach((instance) => {
        this.removeWarranty(instance.variantId);
      });
      this.refreshSubtotal();
      return;
    }

    this.getDispenserInstances().forEach((instance) => {
      const cached = this.offersCache.get(String(instance.variantId));
      const offer = this.pickDefaultOffer(cached?.offers);
      if (offer) {
        this.selectWarranty(instance.variantId, offer);
      } else {
        this.removeWarranty(instance.variantId);
      }
    });
    this.syncWarrantyOfferIdsToDom();
  }

  isDispenserVariantContext(container) {
    if (!container) return false;
    return Boolean(
      container.closest?.('[data-product-container]') ||
      container.closest?.('[data-additional-dispender-wrap] .shopify-section') ||
        container.closest?.('#shopify-section-variant-selection') ||
        container.querySelector?.('[data-dispenser-id], [data-dispender-buy-btn]')
    );
  }

  observeDispenserDom() {
    const wrap = document.querySelector('[data-additional-dispender-wrap]');
    if (!wrap || this.dispenserObserver) return;

    this.dispenserObserver = new MutationObserver(() => {
      if (this.initialized) this.syncFromDom();
    });
    this.dispenserObserver.observe(wrap, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Selected dispenser variants only (Req 2 & 3 — not accessories/upsells)
  // ---------------------------------------------------------------------------

  getAdditionalDispenserSections() {
    const wrap = document.querySelector('[data-additional-dispender-wrap]');
    if (!wrap) return [];
    return [...wrap.querySelectorAll('.shopify-section')];
  }

  getDispenserInstanceKey(section, index) {
    return section.getAttribute('data-warranty-owner-key') || `dispenser-${index + 1}`;
  }

  getDispenserInstances() {
    const instances = [];
    const seenSections = new Set();

    const mainInput = document.querySelector(
      '[data-product-container] [data-dispender-buy-btn], [data-dispender-buy-btn]'
    );
    if (mainInput?.value) {
      // The variant isn't in the config when the page's dispenser isn't the configured product, e.g. on the cat PDP
      // the cart drawer's config is the dog dispenser's (dog in cart): skip it rather than throw on variant.id,
      // which aborted ProductForm.onSubmitHandler before /cart/add.js and left Add to Cart loading
      const variant = this.resolveVariant(mainInput.value);
      if (variant && variant.available !== false) {
        instances.push({
          instanceKey: 'main',
          variantId: String(variant.id),
          variant,
          displayTitle: this.getVariantDisplayTitle(variant)
        });
      }
    }

    this.getAdditionalDispenserSections().forEach((section, index) => {
      if (seenSections.has(section)) return;
      seenSections.add(section);

      const input = section.querySelector('[data-dispenser-id]');
      if (!input?.value) return;

      const variant = this.resolveVariant(input.value);
      if (!variant || variant.available === false) return;

      instances.push({
        instanceKey: this.getDispenserInstanceKey(section, index),
        variantId: String(variant.id),
        variant,
        displayTitle: this.getVariantDisplayTitle(variant)
      });
    });

    const selectedProducts = instances.map((i) => ({
      instanceKey: i.instanceKey,
      variantId: i.variantId,
      sku: i.variant.sku,
      title: i.variant.title,
      price: i.variant.price / 100,
      quantity: 1
    }));
    console.log('Selected Products', selectedProducts);

    return instances;
  }

  resolveVariant(variantId) {
    return (this.config?.variants || []).find((v) => String(v.id) === String(variantId));
  }

  getVariantDisplayTitle(variant) {
    const color =
      variant.title && variant.title !== 'Default Title' ? variant.title : 'your dispenser';
    return color;
  }

  buildProductInfo(variant) {
    const variantTitle =
      variant.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
    const price = (variant.price / 100).toFixed(2);
    const productId = (variant.sku && String(variant.sku).trim()) || String(variant.id);

    if (!productId) {
      console.warn('[mulberry] missing SKU and variant id for productInfo');
    }

    const productInfo = {
      id: productId,
      title: `${this.config.product.title}${variantTitle}`,
      price,
      meta: {
        breadcrumbs: [{ category: this.config.product.category || 'Products' }]
      }
    };

    const imageUrl = this.ensureHttpsUrl(
      variant.image || this.config.product.featuredImage
    );
    if (imageUrl) {
      productInfo.images = [{ src: imageUrl }];
    } else {
      console.warn('[mulberry] no image for variant', variant.id);
    }

    return productInfo;
  }

  // ---------------------------------------------------------------------------
  // Reusable API (Technical Req 1)
  // ---------------------------------------------------------------------------

  async fetchWarrantyOffers(productInfo) {
    if (!window.mulberry?.core?.getWarrantyOffer) {
      throw new Error('[mulberry] SDK not loaded');
    }

    console.log('Mulberry Product Info', productInfo);

    const payloads = this.buildOfferRequestPayloads(productInfo);
    let lastError = null;

    for (const payload of payloads) {
      try {
        const response = await window.mulberry.core.getWarrantyOffer(payload);
        const offers = this.normalizeOffers(response);
        if (offers.length) {
          console.log('Mulberry Offers', offers);
          return offers;
        }
      } catch (error) {
        lastError = error;
        console.warn('[mulberry] getWarrantyOffer failed for id', payload.id, error);
      }
    }

    if (lastError) throw lastError;
    console.log('Mulberry Offers', []);
    return [];
  }

  buildOfferRequestPayloads(productInfo) {
    const ids = [productInfo.id];
    if (this.config?.product?.id) {
      ids.push(String(this.config.product.id));
    }
    return [...new Set(ids.filter(Boolean))].map((id) => ({ ...productInfo, id }));
  }

  renderWarrantyOffers(instanceKey, variantId, offers, displayTitle) {
    const root = document.querySelector('[data-mulberry-variants-root]');
    if (!root) return;

    let block = root.querySelector(`[data-mulberry-instance="${instanceKey}"]`);
    if (!block) {
      block = document.createElement('div');
      block.className = 'mulberry-protection';
      block.setAttribute('data-mulberry-instance', instanceKey);
      block.setAttribute('data-mulberry-variant-id', variantId);
      root.appendChild(block);
    }

    block.dataset.mulberryVariantId = variantId;
    block.classList.remove('mulberry-protection--loading');

    if (!offers?.length) {
      block.innerHTML = `
        <h4 class="mulberry-protection__title">Protect ${this.escapeHtml(displayTitle)} Houndsy Kibble Dispenser</h4>
        <p class="mulberry-protection__status">Protection plans are not available for this selection right now.</p>
      `;
      return;
    }

    const selectedId =
      window.selectedMulberryWarranties[String(variantId)]?.warranty_offer_id || '';

    const offerItems = offers
      .map((offer) => {
        const offerId = offer.warranty_offer_id || offer.id;
        if (!offerId) return '';

        const planName = this.getPlanLabel(offer);
        const priceCents = this.getOfferPriceCents(offer, variantId);
        const recommended = offer.recommended ? '<span class="mulberry-protection__badge">Recommended</span>' : '';
        const checked = String(selectedId) === String(offerId) ? 'checked' : '';
        const safeOfferId = this.escapeHtml(String(offerId));

        return `
          <li class="mulberry-protection__offer">
            <label class="mulberry-protection__label">
              <input
                type="radio"
                class="mulberry-protection__input"
                name="mulberry-warranty-${instanceKey}"
                id="mulberry-${instanceKey}-${safeOfferId}"
                value="${safeOfferId}"
                data-mulberry-offer-id="${safeOfferId}"
                data-mulberry-price-cents="${priceCents}"
                ${checked}
              />
              <span class="mulberry-protection__plan">${this.escapeHtml(planName)}${recommended}</span>
              <span class="mulberry-protection__price">+${this.formatMoney(priceCents)}</span>
            </label>
          </li>
        `;
      })
      .join('');

    block.innerHTML = `
      <h4 class="mulberry-protection__title">Protect ${this.escapeHtml(displayTitle)} Houndsy Kibble Dispenser</h4>
      <ul class="mulberry-protection__offers" role="radiogroup" aria-label="Protection plans for ${this.escapeHtml(displayTitle)}">
        ${offerItems}
      </ul>
    `;

    if (selectedId) {
      this.resolvedVariantIds.add(String(variantId));
    }

    this.bindOfferInputs(block, variantId, offers);
  }

  bindOfferInputs(block, variantId, offers) {
    block.querySelectorAll('.mulberry-protection__label').forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (!input) return;

      label.addEventListener('mousedown', () => {
        input.dataset.mulberryWasChecked = input.checked ? 'true' : 'false';
      });

      label.addEventListener('click', (event) => {
        if (input.dataset.mulberryWasChecked === 'true') {
          event.preventDefault();
          input.checked = false;
          this.clearWarrantySelection(variantId);
          return;
        }

        const offerId = input.getAttribute('data-mulberry-offer-id');
        if (!offerId) return;

        const offer = offers.find(
          (o) => String(o.warranty_offer_id || o.id) === String(offerId)
        );
        if (!offer) {
          console.warn('[mulberry] missing warranty_offer_id on selection');
          return;
        }
        this.selectWarranty(variantId, offer);
      });
    });
  }

  clearWarrantySelection(variantId) {
    this.resolvedVariantIds.delete(String(variantId));
    this.removeWarranty(variantId);
    this.refreshSubtotal();
  }

  selectWarranty(variantId, warranty) {
    const offerId = this.getOfferId(warranty);
    if (!offerId) {
      console.warn('[mulberry] cannot select warranty without warranty_offer_id');
      return;
    }

    const existing = window.selectedMulberryWarranties[String(variantId)];
    if (existing?.warranty_offer_id === offerId) {
      console.log('[mulberry] duplicate selection ignored', variantId, offerId);
      return;
    }

    const entry = {
      warranty_offer_id: String(offerId),
      plan_name: this.getPlanLabel(warranty),
      priceCents: this.getOfferPriceCents(warranty, variantId),
      duration_months: warranty.duration_months
        ? String(warranty.duration_months)
        : undefined
    };

    this.resolvedVariantIds.add(String(variantId));
    window.selectedMulberryWarranties[String(variantId)] = entry;
    console.log('Selected Warranty', { variantId, warranty: entry });
    console.log('Selected Warranties', window.selectedMulberryWarranties);
    this.refreshSubtotal();
  }

  removeWarranty(variantId) {
    delete window.selectedMulberryWarranties[String(variantId)];
    this.resolvedVariantIds.delete(String(variantId));
    console.log('Selected Warranties', window.selectedMulberryWarranties);
  }

  markVariantResolved(variantId) {
    this.resolvedVariantIds.add(String(variantId));
  }

  getSelectedWarranties() {
    return Object.entries(window.selectedMulberryWarranties).map(([variantId, data]) => ({
      variantId,
      warranty_offer_id: data.warranty_offer_id,
      plan_name: data.plan_name,
      price: (Number(data.priceCents) || 0) / 100
    }));
  }

  getSelectedWarrantySubtotalCents() {
    return this.getDispenserInstances().reduce((sum, instance) => {
      const warranty = window.selectedMulberryWarranties[String(instance.variantId)];
      return sum + (Number(warranty?.priceCents) || 0);
    }, 0);
  }

  refreshSubtotal() {
    document.querySelector('variant-selects')?.calculateSubtotal?.();
    window.templateProductJS?.calculateSubtotal?.();
  }

  /** Cart workflow: [{ variantId, warranty_offer_id }, ...] */
  getSelectedWarrantiesForCart() {
    return this.getSelectedWarranties()
      .filter((row) => row.warranty_offer_id)
      .map(({ variantId, warranty_offer_id }) => ({ variantId, warranty_offer_id }));
  }

  getOfferId(offer) {
    if (!offer) return null;
    const id =
      offer.warranty_offer_id ||
      offer.warrantyOfferId ||
      offer.offer_id ||
      offer.id;
    return id ? String(id) : null;
  }

  getWarrantyOfferIdFromCache(variantId) {
    const offer = this.pickDefaultOffer(this.offersCache.get(String(variantId))?.offers);
    return this.getOfferId(offer);
  }

  getWarrantyOfferIdForVariant(variantId) {
    const key = String(variantId);
    const stored = window.selectedMulberryWarranties[key]?.warranty_offer_id;
    if (stored) return stored;

    const cachedOfferId = this.getWarrantyOfferIdFromCache(key);
    if (!cachedOfferId) return null;

    const offer = this.pickDefaultOffer(this.offersCache.get(key)?.offers);
    if (offer) this.selectWarranty(variantId, offer);
    return cachedOfferId;
  }

  syncWarrantyOfferIdsToDom() {
    const variantInput = document.querySelector(
      '[data-warranty-card][data-warranty-owner-key="main"] [data-warranty-variant-id]'
    );
    if (!variantInput) return;

    const attr = variantInput.getAttribute('data-warranty-variant-data');
    if (!attr) return;

    try {
      const data = JSON.parse(attr);
      if (!Array.isArray(data) || !data.length) return;

      let changed = false;
      const updated = data.map((row) => {
        const kibbleId = row.kibbleVariantId ? String(row.kibbleVariantId) : '';
        if (!kibbleId) return row;

        const offerId = this.getWarrantyOfferIdForVariant(kibbleId);
        if (!offerId || row.warrantyOfferId === offerId) return row;

        changed = true;
        return { ...row, warrantyOfferId: offerId };
      });

      if (changed) {
        variantInput.setAttribute('data-warranty-variant-data', JSON.stringify(updated));
      }
    } catch (error) {
      console.error('[mulberry] failed to sync warranty offer ids to form', error);
    }
  }

  /** @deprecated Use getWarrantyOfferIdForVariant — kept for any legacy callers */
  getSelectedWarrantyOfferId() {
    const cartRows = this.getSelectedWarrantiesForCart();
    return cartRows[0]?.warranty_offer_id || null;
  }

  // ---------------------------------------------------------------------------
  // Sync UI when dispensers added / removed / changed (Technical Req 3)
  // ---------------------------------------------------------------------------

  async syncFromDom() {
    if (!this.initialized || this.syncing) return;
    this.syncing = true;

    try {
      const instances = this.getDispenserInstances();
      const activeVariantIds = new Set(instances.map((i) => i.variantId));

      Object.keys(window.selectedMulberryWarranties).forEach((variantId) => {
        if (!activeVariantIds.has(variantId)) {
          this.removeWarranty(variantId);
          this.offersCache.delete(variantId);
        }
      });
      [...this.resolvedVariantIds].forEach((variantId) => {
        if (!activeVariantIds.has(variantId)) this.resolvedVariantIds.delete(variantId);
      });

      for (const instance of instances) {
        this.instanceVariantMap.set(instance.instanceKey, instance.variantId);

        const productInfo = this.buildProductInfo(instance.variant);
        const cacheKey = String(instance.variantId);
        let cached = this.offersCache.get(cacheKey);
        const prevVariantForInstance = this.prevVariantByInstanceKey.get(instance.instanceKey);
        const variantChanged = prevVariantForInstance && prevVariantForInstance !== instance.variantId;

        if (variantChanged) {
          const preserved = window.selectedMulberryWarranties[instance.variantId];
          if (!preserved) this.removeWarranty(prevVariantForInstance);
        }

        if (!cached || variantChanged) {
          try {
            const offers = await this.fetchWarrantyOffers(productInfo);
            cached = { offers, productInfo };
            this.offersCache.set(cacheKey, cached);
          } catch (error) {
            console.error('[mulberry] fetch failed', instance.variantId, error);
            cached = { offers: [], productInfo };
            this.offersCache.set(cacheKey, cached);
          }
        }

        this.prevVariantByInstanceKey.set(instance.instanceKey, instance.variantId);
      }

      this.syncCareWarrantySelections();
      this.syncWarrantyOfferIdsToDom();
      this.updateCareCardVisibility();
    } finally {
      this.syncing = false;
    }
  }

  renderLoading(instanceKey, displayTitle) {
    const root = document.querySelector('[data-mulberry-variants-root]');
    if (!root) return;

    let block = root.querySelector(`[data-mulberry-instance="${instanceKey}"]`);
    if (!block) {
      block = document.createElement('div');
      block.className = 'mulberry-protection mulberry-protection--loading';
      block.setAttribute('data-mulberry-instance', instanceKey);
      root.appendChild(block);
    }

    block.innerHTML = `
      <h4 class="mulberry-protection__title">Protect ${this.escapeHtml(displayTitle)} Houndsy Kibble Dispenser</h4>
      <p class="mulberry-protection__status">Loading protection plans…</p>
    `;
  }

  // ---------------------------------------------------------------------------
  // SDK helpers
  // ---------------------------------------------------------------------------

  waitForSdk() {
    if (window.__mulberrySdkReady) return window.__mulberrySdkReady;

    return new Promise((resolve, reject) => {
      let attempts = 0;
      const poll = () => {
        if (window.mulberry?.core?.getWarrantyOffer) {
          resolve();
          return;
        }
        if (attempts++ >= 100) {
          reject(new Error('[mulberry] SDK load timeout'));
          return;
        }
        setTimeout(poll, 100);
      };
      poll();
    });
  }

  normalizeOffers(response) {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.offers)) return response.offers;
    if (Array.isArray(response.warranties)) return response.warranties;
    if (Array.isArray(response.data)) return response.data;
    if (response.warranty_offer_id || response.id) return [response];
    return [];
  }

  getInitOptions() {
    const isStaging = this.config.environment === 'staging';
    return {
      publicToken: this.config.publicToken || window.mulberryPublicToken,
      isStaging,
      api_endpoint: isStaging
        ? 'https://partner-staging.getmulberry.com'
        : 'https://partner.getmulberry.com',
      sessionId: `shopify-${Date.now()}`
    };
  }

  clearMulberryCache() {
    ['warranty_settings', 'mulberry_warranty_settings'].forEach((key) => {
      try {
        window.localStorage?.removeItem(key);
        window.sessionStorage?.removeItem(key);
      } catch (_) {
        /* ignore */
      }
    });
  }

  isHasGeoCheckInitError(error) {
    return String(error?.message || error || '').includes('has_geo_check');
  }

  async runCoreInit() {
    await window.mulberry.core.init(this.getInitOptions());
    if (!window.mulberry?.core?.settings) {
      throw new Error('[mulberry] init did not return retailer settings');
    }
  }

  async ensureCoreInit() {
    if (window.mulberry?.core?.settings) return;

    if (!this.coreInitPromise) {
      this.coreInitPromise = this.runCoreInit().catch(async (error) => {
        this.coreInitPromise = null;
        if (!this.coreInitRetried && this.isHasGeoCheckInitError(error)) {
          this.coreInitRetried = true;
          this.clearMulberryCache();
          return this.ensureCoreInit();
        }
        throw error;
      });
    }

    await this.coreInitPromise;
  }

  markOffersWrapState(hasOffers, showFallback = false) {
    const wrap = document.querySelector('[data-mulberry-offers-wrap]');
    const fallback = document.querySelector('[data-mulberry-fallback], [data-mulberry-care-card]');

    if (wrap) {
      wrap.toggleAttribute('data-mulberry-empty', !hasOffers);
      wrap.toggleAttribute('data-mulberry-ready', hasOffers);
    }

    if (fallback) {
      fallback.classList.add('d-none');
      fallback.toggleAttribute('hidden', true);
    }
  }

  isCareCardVisible() {
    if (!this.initialized) return false;

    const instances = this.getDispenserInstances();
    if (!instances.length) return false;

    return instances.some((instance) => {
      const cached = this.offersCache.get(String(instance.variantId));
      return cached?.offers?.length > 0;
    });
  }

  updateCareCardVisibility() {
    const shouldShow = this.isCareCardVisible();

    document
      .querySelectorAll('[data-mulberry-care-card], [data-dispenser-warranty-stack] [data-warranty-card]')
      .forEach((card) => {
        card.classList.toggle('d-none', !shouldShow);
        card.toggleAttribute('hidden', !shouldShow);
      });
  }

  // ---------------------------------------------------------------------------
  // Display helpers
  // ---------------------------------------------------------------------------

  getPlanLabel(offer) {
    const months = offer.duration_months;
    if (months) {
      const years = Math.round(Number(months) / 12);
      if (years >= 1) return `${years} Year Protection`;
    }
    return offer.product?.name || offer.plan_name || 'Protection Plan';
  }

  getWarrantySeriesMap() {
    if (this._warrantySeriesMap !== undefined) return this._warrantySeriesMap;

    const mapScript = document.querySelector('[data-warranty-series-map]');
    if (!mapScript?.textContent?.trim()) {
      this._warrantySeriesMap = null;
      return null;
    }

    try {
      this._warrantySeriesMap = JSON.parse(mapScript.textContent);
    } catch (error) {
      console.error('[mulberry] invalid warranty series map', error);
      this._warrantySeriesMap = null;
    }

    return this._warrantySeriesMap;
  }

  getWarrantySeriesMapFromRoot(root = document) {
    const scope = root instanceof Element ? root : document;
    const mapScript = scope.querySelector('[data-warranty-series-map]');
    if (!mapScript?.textContent?.trim()) return this.getWarrantySeriesMap();

    try {
      return JSON.parse(mapScript.textContent);
    } catch (error) {
      console.error('[mulberry] invalid warranty series map', error);
      return null;
    }
  }

  resolveVariantFromCartItem(item) {
    const fromConfig = this.config?.variants?.find((variant) => String(variant.id) === String(item.variant_id));
    if (fromConfig) return fromConfig;

    return {
      id: item.variant_id,
      title: item.variant_title,
      price: item.price,
      sku: item.sku,
      available: true
    };
  }

  async ensureCartDrawerReady() {
    if (!document.getElementById('mulberry-product-config')) return false;
    if (!this.initialized) {
      await this.bootstrap();
    }
    return this.initialized;
  }

  async buildCartDrawerWarrantyItems(form) {
    const root = form?.closest('[data-cart-upsell-mulberry]') || form;
    const targetsScript = root?.querySelector?.('[data-cart-upsell-warranty-targets]');
    const seriesMap = this.getWarrantySeriesMapFromRoot(root);
    if (!targetsScript?.textContent?.trim() || !seriesMap) return [];

    let targets = [];
    try {
      targets = JSON.parse(targetsScript.textContent);
    } catch (error) {
      console.error('[mulberry] invalid cart upsell warranty targets', error);
      return [];
    }

    if (!Array.isArray(targets) || !targets.length) return [];

    await this.ensureCartDrawerReady();

    const cartItems = window.globalSpace?.cart?.items || [];
    const linkedLineKeys = new Set(
      cartItems
        .map((item) => {
          const props = item?.properties;
          if (!props) return '';
          if (Array.isArray(props)) {
            const entry = props.find((row) => row?.key === '_warranty_for_line_key');
            return entry?.value ? String(entry.value) : '';
          }
          return props._warranty_for_line_key ? String(props._warranty_for_line_key) : '';
        })
        .filter(Boolean)
    );

    const addItems = [];
    for (const target of targets) {
      const kibbleVariantId = String(target.kibbleVariantId || '');
      const kibbleLineKey = target.kibbleLineKey ? String(target.kibbleLineKey) : '';
      const series = target.series;
      const config = series && seriesMap[series];
      const warrantyVariantId = parseInt(target.warrantyVariantId || config?.variantId, 10);
      if (!kibbleVariantId || Number.isNaN(warrantyVariantId)) continue;
      if (kibbleLineKey && linkedLineKeys.has(kibbleLineKey)) continue;

      const cartLine = window.globalSpace?.cart?.items?.find((item) =>
        kibbleLineKey ? item.key === kibbleLineKey : String(item.variant_id) === kibbleVariantId
      );
      const variant =
        this.resolveVariant(kibbleVariantId) ||
        (cartLine ? this.resolveVariantFromCartItem(cartLine) : null);

      if (this.initialized && variant) {
        let cached = this.offersCache.get(kibbleVariantId);
        if (!cached) {
          try {
            const productInfo = this.buildProductInfo(variant);
            const offers = await this.fetchWarrantyOffers(productInfo);
            cached = { offers, productInfo };
            this.offersCache.set(kibbleVariantId, cached);
          } catch (error) {
            console.error('[mulberry] cart upsell offer fetch failed', kibbleVariantId, error);
            cached = { offers: [], productInfo: this.buildProductInfo(variant) };
            this.offersCache.set(kibbleVariantId, cached);
          }
        }

        const offer = this.pickDefaultOffer(cached?.offers);
        if (offer) this.selectWarranty(kibbleVariantId, offer);
      }

      const unitPrice = parseInt(target.unitPrice || config?.price || '0', 10);
      const offerId = this.getWarrantyOfferIdForVariant(kibbleVariantId);
      const warrantyProps = {
        _originalPrice: unitPrice,
        _compareAtPrice: config?.compareAtPrice || unitPrice,
        _timestamp: new Date().toISOString() + '-' + Math.random().toString(36).substring(2, 8),
        _warranty_for_variant_id: kibbleVariantId,
        _warranty_series: series
      };
      if (kibbleLineKey) warrantyProps._warranty_for_line_key = kibbleLineKey;
      if (offerId) warrantyProps._warranty_offer_id = String(offerId);

      const warrantyItem = {
        id: warrantyVariantId,
        quantity: 1,
        properties: warrantyProps
      };
      // Nested cart line (Ajax Cart API): the parent dispenser already exists in the cart,
      // so link the child by parent_line_key. Top-level param, not a property.
      if (kibbleLineKey) warrantyItem.parent_line_key = kibbleLineKey;
      addItems.push(warrantyItem);
    }

    return addItems;
  }

  resolveVariantSeries(variant) {
    const candidates = [variant?.title, variant?.option1, variant?.option2, variant?.option3]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase());

    if (candidates.some((value) => value.includes('essential'))) return 'essential';
    if (candidates.some((value) => value.includes('furniture'))) return 'furniture';
    return null;
  }

  getOfferPriceCents(offer, variantId) {
    const variant = this.resolveVariant(variantId);
    const series = this.resolveVariantSeries(variant);
    const seriesMap = this.getWarrantySeriesMap();
    const configuredCents = series && seriesMap?.[series]?.price;

    if (configuredCents) return Number(configuredCents);

    const raw = offer.customer_cost ?? offer.cost ?? 0;
    const dollars = parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
    return Math.round(dollars * 100);
  }

  parseOfferPrice(offer) {
    return this.getOfferPriceCents(offer) / 100;
  }

  formatMoney(cents) {
    if (window.Utility?.formatMoney && window.globalSpace?.money_format) {
      return window.Utility.formatMoney(cents, window.globalSpace.money_format);
    }
    return `$${(Number(cents) / 100).toFixed(2)}`;
  }

  ensureHttpsUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

window.mulberryIntegration = new MulberryIntegration();

const integration = window.mulberryIntegration;
window.fetchWarrantyOffers = (productInfo) => integration.fetchWarrantyOffers(productInfo);
window.renderWarrantyOffers = (variantId, offers, displayTitle = 'your') => {
  const instanceKey = `variant-${variantId}`;
  integration.renderWarrantyOffers(instanceKey, String(variantId), offers, displayTitle);
};
window.selectWarranty = (variantId, warranty) => integration.selectWarranty(variantId, warranty);
window.removeWarranty = (variantId) => integration.removeWarranty(variantId);
window.getSelectedWarranties = () => integration.getSelectedWarranties();

function startMulberryIntegration() {
  window.mulberryIntegration.init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startMulberryIntegration);
} else {
  startMulberryIntegration();
}
